import { Button } from "@/components/custom/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { client } from "@/client";
import { useTranslation } from "react-i18next";
import { Route } from "@/routes/dashboard/reports/$reportId/route.tsx";
import SelectInputCreatable from "@/components/select-input/select-input-creatable.tsx";

import { showActionErrorToast } from "@/state/error-toast";
import { showEmailOutcomeToast } from "@/state/email-outcome-toast";

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
});
const reportFormSchema = z.object({
  mailingList: z.array(optionSchema),
});

interface InviteUsersProps {
  filters: string;
}

export const InviteUsers = ({ filters }: InviteUsersProps) => {
  const { reportId } = Route.useRouteContext();
  const { t } = useTranslation();

  const inviteUsersToReport = useMutation({
    ...client.users.inviteUsersToReport.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: (outcome) =>
      showEmailOutcomeToast(outcome, {
        partial: t("inviteEmailsPartiallyFailed"),
        success: t("successfullyInvitedUsers"),
      }),
  });

  const form = useForm<z.infer<typeof reportFormSchema>>({
    resolver: zodResolver(reportFormSchema),
  });

  const onSubmit = async (data: z.infer<typeof reportFormSchema>) => {
    await inviteUsersToReport.mutateAsync({
      requestBody: {
        mailingList: data.mailingList.map((o) => o.value),
        reportId,
        filters,
      },
    });
    document.getElementById("reports-invite-cancel")?.click();
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{t("inviteUsers")}</DialogTitle>
          </DialogHeader>
          <div className="p-6">
            <FormField
              control={form.control}
              name="mailingList"
              render={({ field }) => (
                <FormItem className="py-4">
                  <FormLabel>{t("inviteeList")}</FormLabel>
                  <FormControl>
                    <SelectInputCreatable
                      {...field}
                      isMulti={true}
                      createLabel="Invite"
                      placeholder={t("typeEmails")}
                    />
                  </FormControl>
                  <FormMessage>
                    {form.formState.errors.mailingList?.message}
                  </FormMessage>
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" id="reports-invite-cancel">
                {t("cancel")}
              </Button>
            </DialogClose>
            <Button
              loading={inviteUsersToReport.isPending}
              variant={"azure"}
              id="reports-invite-save"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
};
